/**
 * @fileoverview Instructions component for displaying formatted instruction text with optional icon
 * Renders markdown-formatted instructions within a styled container.
 *
 * @example
 * // Basic usage
 * <Instructions instructions="Follow these steps..." />
 *
 * @example
 * // With icon
 * <Instructions
 *   instructions="Important instructions..."
 *   options={{
 *     icon: true,
 *     alt: "Information icon"
 *   }}
 * />
 */


import { PropsWithChildren } from 'react'
import ReactMarkdown from 'react-markdown'

import config from '../../../config'
import css from './Instructions.module.css'

/**
 * @interface InstructionProps
 * @description Props for the Instructions component
 * @property {string} instructions - The instructions to display
 * @property {Record<string, unknown>} options - Optional options for the component
 */
interface InstructionProps extends PropsWithChildren {
    instructions: string
    options?: Record<string, unknown>
}

/**
 * @function Instructions
 * @description Component for displaying formatted instruction text with optional icon
 * @param {InstructionProps} props - The props for the component
 * @returns {JSX.Element} The Instructions component
 */
export function Instructions({ instructions, options = {} }: InstructionProps) {
    const { icon } = options

    return (
        <div className={css.container}>
            <div className={css.instructions}>
                {icon && (
                    <div>
                        <img
                            src={config.s3.getStaticURL('explam.svg')}
                            alt={options.alt as string}
                        />
                    </div>
                )}
                <div data-testid="instructions-container">
                    {instructions ? <ReactMarkdown>{instructions}</ReactMarkdown> : null}
                </div>
            </div>
        </div>
    )
}
